import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';

// Flat monthly operating-cost fee, ex-VAT, per physical system a customer
// operates. Charged the same way every month — there is no per-system rate
// override (unlike garantiomsetning, which is a negotiated figure per
// system): this is a flat internal cost recovery fee.
export const DRIFTKOSTNAD_RATE_EX_VAT = 59;

export interface DriftkostnadBillable {
  systemCount: number;
  billable: number;   // systemCount * DRIFTKOSTNAD_RATE_EX_VAT, ex-VAT
  systems: { systemId: string, name: string }[];
}

// Per-customer driftkostnader (operating cost) — one flat DRIFTKOSTNAD_RATE_EX_VAT
// charge per month for every system the customer operates. "Operates" is
// resolved the SAME way Garantiomsetning resolves system ownership: the
// system-kunder link table (see GarantiomsetningService.getGarantiSystems for
// the identical join) — that table is this codebase's one canonical answer to
// "which customer does this physical system belong to". A system linked to
// several customers (system-kunder's 'shared' case) isn't split here, same
// simplification GarantiomsetningService makes today.
//
// A customer can opt OUT entirely via the "Driftskostnader" toggle on their
// kundekort (Operations tab) — `driftkostnaderEnabled` on the `kunder` doc.
// Defaults to ON when the field is absent (matches "charged from all" as the
// baseline; the toggle exists for the exceptions).
//
// Not month-scoped: reflects TODAY's system↔customer links and each system's
// current `active` flag, not a historical snapshot for the selected month —
// there is no dated history of when a system/link started or stopped, so a
// past month is billed on present-day state. Good enough for a flat recurring
// fee generated close to the month it covers; flag to the user if that ever
// stops being true (e.g. billing several months late for a decommissioned site).
@Injectable({ providedIn: 'root' })
export class DriftkostnadService {

  constructor(private afs: AngularFirestore) { }

  async getBillableByCustomer(): Promise<{ [kundekortId: string]: DriftkostnadBillable }> {
    const [sysSnap, linkSnap, kunderSnap] = await Promise.all([
      this.afs.collection('system_info').ref.get(),
      this.afs.collection('system-kunder').ref.get(),
      this.afs.collection('kunder').ref.get()
    ]);

    // A link whose kundekort was deleted is not an owner. Deleting a customer
    // leaves its system-kunder links behind, and because the map below is
    // last-write-wins, a dead link can overwrite a live one and make the system
    // resolve to a customer that no longer exists — so re-linking it by hand in
    // the admin panel would appear to work and change nothing.
    const liveCustomer = new Set(kunderSnap.docs.map(d => d.id));
    const linkBySystem: { [systemId: string]: string } = {};
    linkSnap.docs.forEach(d => {
      const l = d.data() as any;
      if (l.systemId && l.kundekortId && liveCustomer.has(l.kundekortId)) { linkBySystem[l.systemId] = l.kundekortId; }
    });

    // Opt-out only — see class doc comment above.
    const enabledByCustomer: { [id: string]: boolean } = {};
    kunderSnap.docs.forEach(d => {
      const v = d.data() as any;
      enabledByCustomer[d.id] = v.driftkostnaderEnabled !== false;
    });

    const out: { [kundekortId: string]: DriftkostnadBillable } = {};
    sysSnap.docs.forEach(d => {
      const s = d.data() as any;
      if (s.active === false) { return; }   // explicitly switched off — not operating, not billed
      const kundekortId = linkBySystem[d.id] || (s.systemId ? linkBySystem[s.systemId] : null) || null;
      if (!kundekortId) { return; }          // nowhere to bill an unlinked system
      if (enabledByCustomer[kundekortId] === false) { return; }   // customer opted out

      const bucket = out[kundekortId] || (out[kundekortId] = { systemCount: 0, billable: 0, systems: [] });
      bucket.systemCount += 1;
      bucket.systems.push({ systemId: d.id, name: s.name || d.id });
    });
    Object.values(out).forEach(b => { b.billable = +(b.systemCount * DRIFTKOSTNAD_RATE_EX_VAT).toFixed(2); });
    return out;
  }
}
